import React, {useCallback, useEffect, useState} from 'react';
import {useTableState, useTimeout} from '../../custom-hooks';
import Grid from '@material-ui/core/Grid';
import {isViewerUser, showError} from '../../helpers';
import Loader from '../common/loader';
import CTable from '../common/ctable';
import {columns} from './cameras-table-def';
import CamerasFilter from './cameras-filter';
import AppStore from '../../stores/app-store';
import AppActions from '../../actions/app-actions';
import ReactDOM from 'react-dom';
import CamerasExpanded from './cameras-expanded';
import CamerasDialog from './cameras-dialog';
import { AppContext } from '../../contexts/app-context';

const timeoutInMilliSec = 30 * 1000;
const columnDefinition = columns();

const Cameras = (props) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false });
  const [deviceList, setDeviceList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [startTimer] = useTimeout(refreshData, timeoutInMilliSec);
  const [filterselectedItem, setFilterSelectedItem] = useState('');
  const [filterSelectedType, setFilterSelectedType] = useState('All');
  const { colDef, selectedRows, onRowSelect, onAllRowSelect, onSelectedRowsReset, onFilterChange, onColumnChange } = useTableState(columnDefinition);

  const body = () => {
    if(loading) {
      return loader();
    } else {
      return (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {table()}
            </Grid>
          </Grid>
          {dlg.open ? <CamerasDialog {...dlg} /> : null}
        </>
      );
    }
  };

  const loader = () => (<div><Loader show={true} /></div>);

  function table () {
    return (
      <div className='tab-container vcam-container'>
        <div className='leftFixed'>
          <CamerasFilter
            devices={deviceList}
            onAllSelected={onAllSelected}
            onDeviceSelected={onDeviceSelected}
            filterSelectedType={filterSelectedType}
            filterselectedItem={filterselectedItem} />
        </div>
        <div className='rightFluid vcam'>
          {pageLoading ? loader() : viewTable()}
        </div>
      </div>
    );
  }

  function viewTable() {
    return (
      <AppContext.Consumer>
        {({ updateRedirect }) => (
          <CTable
            data={list}
            colDef={colDef}
            onDelete={onDelete}
            onExpand={onExpand}
            redirect={props.redirect}
            noOfNonSelectableRows={1}
            selectedRows={selectedRows}
            onFilterChange={onFilterChange}
            onColumnChange={onColumnChange}
            updateRedirect={updateRedirect}
            onRowSelect={!isViewerUser() ? onRowSelect : null}
            onAllRowSelect={!isViewerUser() ? onAllRowSelect : null}
          />
        )}
      </AppContext.Consumer>
    );
  }

  const onExpand = useCallback(rowData => {
    return (
      <AppContext.Consumer>
        {({ updateRedirect }) => (
          <CamerasExpanded {...rowData} onUpdate={onUpdate} redirect={props.redirect} updateRedirect={updateRedirect}/>
        )}
      </AppContext.Consumer>
    )
  }, [onUpdate]);

  const onUpdate = useCallback(data => {
    const newList = list.map(item => item.id === data.id ? {...item, ...data } : item);
    setList(newList);
  }, [list]);

  const onAllSelected = useCallback(() => {
    setPageLoading(true);
    setFilterSelectedType('All');
    setFilterSelectedItem('');
    AppActions.getAllCameras()
      .then(cameras => {
        ReactDOM.unstable_batchedUpdates(() => {
          const dataList = cameras || [];
          setList(dataList);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Cant' get the cameras`);
      });
  }, []);

  const onDeviceSelected = useCallback(selected => {
    setPageLoading(true);
    setFilterSelectedType('By Device');
    setFilterSelectedItem(selected);
    AppActions.getAllCameras()
      .then(cameras => {
        ReactDOM.unstable_batchedUpdates(() => {
          const dataList = cameras || [];
          setList(dataList);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Can't get the cameras`);
      });
  });

  const onDelete = useCallback(() => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      const delPromises = selectedRows.map(sr => AppActions.removeCameras(sr.id));
      if(delPromises.length > 0) {
        Promise.all(delPromises)
          .then(() => {
            const newList = list.filter(
              fId => (selectedRows.findIndex(srId => fId.id === srId.id)) === -1
            );
            ReactDOM.unstable_batchedUpdates(() => {
              setList(newList);
              onSelectedRowsReset();
            });
            AppActions.setSnackbar('Cameras deleted successfully');
          })
          .catch(err => showError(err, `Couldn't delete cameras`))
          .finally(() => {
            setDlg(() => ({open: false}));
          });
      }
      else {
        onSelectedRowsReset();
        setDlg(() => ({open: false}));
        AppActions.setSnackbar('could not delete the physcal camera, is any selected?');
      }
    };
    setDlg({ open: true, handleType: 'remove', handleOk, handleCancel });
  }, [selectedRows, list]);

  function fetchDetails(didMount) {
    const hideLoading = () => {
      if(didMount)
        setLoading(() => false);
    };

    Promise.all([AppActions.getAllCameras(), AppActions.getUserOrganization()])
      .then(responses => {
        if(responses !== undefined && responses.length > 1) {
          const dataList = responses[0] || [];
          setDevicesToFilter(dataList);
          setSptsStatus(dataList);
          setList(dataList);
          ReactDOM.unstable_batchedUpdates(() => {
            setList(dataList);
            hideLoading();
          });
        }
      })
      .catch(err => {
        showError(err);
        hideLoading();
      })
      .finally(() => startTimer());
  }

  function refreshData() {
    fetchDetails();
  }

  useEffect(() => {
    fetchDetails(true);
  }, []);

  function setDevicesToFilter(list) {
    list.forEach(item => {
      const devices = [];
      devices.push({route: item.active_route});
      item.alternative_routes?.forEach(alterRoute => devices.push({route: alterRoute}));
      devices.forEach(device => {
        AppActions.getLocalConfigByDeviceUUID(device.route, '').then(response => {
          const label = response.label;
          if(deviceList.length > 0) {
            if(!deviceList.map(d => d.route).includes(device.route))
              deviceList.push({route: device.route, label: label});
          }
          else
            deviceList.push({route: device.route, label: label});
        })
      });
    });
    setDeviceList(deviceList);
  }

  function setSptsStatus(list) {
    let sptsStatus = AppStore.getSptsStatus();
    list.forEach(item => item.sptsStatus = sptsStatus);
    hiddenDstMulticastIP(sptsStatus);
  }

  function hiddenDstMulticastIP(sptsStatus) {
    if(!sptsStatus) {
      colDef
        ?.filter(col => col.title === 'Dst. Multicast IP')
        ?.forEach(col => col.hidden = true)
    }
  }

  return (
    <>
      {body()}
    </>
  );
}

export default Cameras;
